import { useEffect } from 'react';
import { usePagination } from './usePagination';

/**
 * useTablePagination — unified hook for all tables (mirrors ui-kit pagination pattern)
 *
 * Wraps usePagination and adds:
 *  - automatic reset when searchQuery/statusFilter changes
 *  - overflow guard (goToPage(1) when currentPage > totalPages)
 *  - ready-to-spread paginationProps for <Pagination />
 *
 * Client mode (array pagination):
 *   const { paginatedData, paginationProps } = useTablePagination({
 *     data: filteredUsers,
 *     pageSize: 10,
 *     searchQuery,
 *   });
 *
 * Server mode (server pagination, page state managed):
 *   const { paginationProps, currentPage, setPage } = useTablePagination({
 *     server: true,
 *     totalItems: total,
 *     totalPages,
 *     currentPage: page,
 *     onPageChange: setPage,
 *     pageSize: 20,
 *     searchQuery,
 *   });
 *
 * @param {Object} config
 * @param {Array} [config.data] - array to paginate (client mode)
 * @param {boolean} [config.server=false] - true for server mode
 * @param {number} [config.pageSize=10] - rows per page
 * @param {string} [config.searchQuery] - when changed, resets to page 1
 * @param {any} [config.resetKey] - extra dependency that triggers reset (e.g. statusFilter)
 * @param {number} [config.initialPage=1]
 * @param {number} [config.totalItems] - server total
 * @param {number} [config.totalPages] - server totalPages
 * @param {number} [config.currentPage] - server currentPage (controlled)
 * @param {Function} [config.onPageChange] - server setPage
 */
export function useTablePagination(config = {}) {
    const {
        data,
        server = false,
        pageSize = 10,
        searchQuery,
        resetKey,
        initialPage = 1,
        totalItems: serverTotalItemsProp,
        totalPages: serverTotalPagesProp,
        currentPage: controlledPage,
        onPageChange: controlledSetPage,
    } = config;

    // Always call hooks in same order (rules-of-hooks)
    const {
        paginatedItems,
        currentPage: clientCurrentPage,
        totalPages: clientTotalPages,
        totalItems: clientTotalItems,
        goToPage: clientGoToPage,
        pageSize: clientPageSize,
    } = usePagination(data ?? [], pageSize);

    const serverCurrentPage = controlledPage ?? initialPage;
    const serverTotalPages = serverTotalPagesProp ?? 1;
    const serverTotalItemsVal = serverTotalItemsProp ?? 0;

    const currentPage = server ? serverCurrentPage : clientCurrentPage;
    const totalPages = server ? serverTotalPages : clientTotalPages;
    const totalItems = server ? serverTotalItemsVal : clientTotalItems;
    const goToPage = server ? controlledSetPage : clientGoToPage;
    const resolvedPageSize = server ? pageSize : clientPageSize;

    // Overflow guard (client only will have effect, server is no-op if goToPage undefined)
    useEffect(() => {
        if (!server && currentPage > totalPages && totalPages > 0 && goToPage) goToPage(1);
    }, [server, currentPage, totalPages, goToPage]);

    // Reset on search/filter change
    useEffect(() => {
        if (!server && goToPage) goToPage(1);
        if (server && controlledSetPage && (searchQuery !== undefined || resetKey !== undefined)) {
            // no-op: caller should handle via container effect, kept for API symmetry
        }
    }, [server, searchQuery, resetKey, goToPage, controlledSetPage]);

    const paginationProps = {
        currentPage,
        totalPages,
        onPageChange: goToPage,
        itemsPerPage: resolvedPageSize,
        totalItems,
    };

    const paginatedData = server ? (data ?? []) : paginatedItems;

    return {
        paginatedData,
        paginatedItems: paginatedData,
        data: paginatedData,
        currentPage,
        totalPages,
        totalItems,
        pageSize: resolvedPageSize,
        paginationProps,
        goToPage,
        setPage: goToPage,
        reset: () => goToPage?.(1),
    };
}

export default useTablePagination;
